<script setup>
import { APP } from "@/config/app";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery, provideApolloClient } from "@vue/apollo-composable";
import { handleSignOut } from "@/utils/cognito";
import { useCognitoStore } from "@/stores/cognito";
import { corporateRegex } from "@/utils/corporateClients";
import { gqlGetClientInfo, gqlGetClientServices} from "@/graphql/clients";
import { Dialog, DialogPanel } from "@headlessui/vue";
import { Bars3Icon, XMarkIcon } from "@heroicons/vue/24/outline";
import AtomButton from "@Atoms/Button.vue";
import UserIcon from "@/components/svg/User.vue";
import MyWealth from "@/components/layout/MyWealth.vue";
import MenuNav from "@/components/dashboard/MenuNav.vue";
import useNavigationLinks from "../hooks/useNavigationLinks";
import MyWealthInvest from "@/components/layout/MyWealthInvest.vue";
import { useKillSwitchFlagsStore } from "@/stores/storeKillSwitchFlags";
import { useStoreDismissedMessages } from "@/stores/storeDismissedMessages";

import { apolloClient } from "@/utils/apolloClient";
provideApolloClient(apolloClient);

const forceRerender = ref(0);

const guestLinks = [
  { name: 'Global Index Service', href: '/global-index-service' },
  { name: 'Self-Select Service', href: '/self-select-service' },
  { name: 'News', href: '/news' },
  { name: 'Get in touch', href: '/get-in-touch' },
]

const isAuthenticated = computed(function() {
  const storeCognito = useCognitoStore();
  return storeCognito.isAuthenticated;
});

const { result, onResult } = useQuery(gqlGetClientInfo, null, {
  enabled: isAuthenticated
});

const mobileMenuOpen = ref(false);

const regex = corporateRegex;

const {
  result: getClientServicesResult,
  loading: getClientServicesLoading,
  error: getClientServicesError,
  onResult: clientServicesOnResult
} = useQuery(gqlGetClientServices, {}, {
  fetchPolicy: 'no-cache',
  enabled: isAuthenticated
});

const corporateClientServices = computed(() => getClientServicesResult?.value?.clientInfo);

clientServicesOnResult((result) => {
  forceRerender.value += 1;
});

//get access to Vuex router
const router = useRouter();
const route = useRoute();

function logout() {
  handleSignOut();

  router.push({
    name: "Login",
    query: {loggedOut: true}
  });
}
const isRegisteringFromCorporate = computed(function (){
  return route.fullPath.includes('register') && route.query && route.query.src != undefined && regex.test(route.query.src.toString());
})

const messageDismissal = useStoreDismissedMessages();
onResult((result) => {
  if (result.loading) return;
  // store the client type (client_group attribute in Cognito)
  // TODO - Move this to login and get from Cognito User Attributes instead

  storeKillSwitchFlags.appDisabled = result.data.clientInfo.features.appDisabled;
  storeKillSwitchFlags.dashboardDisabled = result.data.clientInfo.features.dashboardDisabled;
  storeKillSwitchFlags.documentsDisabled = result.data.clientInfo.features.documentsDisabled;

  forceRerender.data += 1;
});

const storeKillSwitchFlags = useKillSwitchFlagsStore();
storeKillSwitchFlags.appDisabled = result?.data?.clientInfo?.features?.appDisabled;
storeKillSwitchFlags.dashboardDisabled = result?.data?.clientInfo?.features?.dashboardDisabled;
storeKillSwitchFlags.documentsDisabled = result?.data?.clientInfo?.features?.documentsDisabled;

function toggleMobileMenu(isOpen, event) {
    console.log(isOpen)
    console.log(event)
    if(!isOpen) {
        mobileMenuOpen.value = true;
    }
}

const hasSelfSelectPortfolio = computed(() => {
    let selfSelect = result?.value?.clientInfo?.portfolios?.elements?.filter(portfolio => portfolio.service === 'SELF_SELECT' && portfolio.type === 'ISA')
    return selfSelect?.length > 0
})

const hasPaymentAvailability = computed(() => {
  let paymentAvailability = result?.value?.clientInfo?.portfolios?.elements?.filter(portfolio => portfolio.paymentsAvailability === 'ALLOWED')
  return paymentAvailability?.length > 0
})

const authenticatedLinks = useNavigationLinks({
    type: result?.clientInfo?.type,
    clientInfo: result?.clientInfo,
    killSwitchFlags: storeKillSwitchFlags,
    hasSelfSelectPortfolio: hasSelfSelectPortfolio,
    corporateClientServices: corporateClientServices,
    hasPaymentAvailability: hasPaymentAvailability
});

</script>

<template>
  <header class="bg-white">
    <nav class="flex mx-auto max-w-7xl items-center justify-between p-8 xl:px-0" aria-label="Navigation">
      <!-- Home icon -->
      <div class="flex flex-1">
        <RouterLink
          to="/dashboard"
          aria-label="Home link icon"
          class=""
        >
          <span class="sr-only">my wealth invest</span>

          <picture class="h-12 lg:h-20 w-auto">
            <MyWealthInvest>
              <img
                :srcset="APP.CdnBaseUrl + '/my-wealth-invest-logo-rebrand-light.svg'"
                alt="my wealth invest"
              />
            </MyWealthInvest>
            <MyWealth>
              <img
                :srcset="APP.CdnMwpBaseUrl + '/logos/MWP-logo-2.svg'"
                alt="my wealth private"
                class="-ml-2"
              />
            </MyWealth>
          </picture>
        </RouterLink>
      </div>

      <!-- Mobile nav 3 lines icon -->
      <div
        v-show="!mobileMenuOpen"
        class="flex lg:hidden"
      >
        <button
          @click="toggleMobileMenu(mobileMenuOpen, $event)"
          aria-label="Open main menu"
          class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
        >
          <span class="sr-only">Open main menu</span>
          <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      <!-- Page links, log in, log out -->
      <MyWealthInvest>
        <div class="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-x-12" v-show="!isRegisteringFromCorporate">
          <RouterLink
            v-if="!isAuthenticated"
            v-for="item in guestLinks"
            :key="item.name"
            :to="item.href"
            :class="[APP.MyWealthMode ? 'text-forest' : 'text-teal', 'nav-link']"
            :aria-label="`${item.name} button`"
          >
            {{ item.name }}
          </RouterLink>

          <RouterLink
            v-if="isAuthenticated"
            to="/login"
            @click.prevent="logout"
            class="primary-mwi h-min button-padding body-3-semi-bold rounded-full max-w-max"
            aria-label="Log out button"
          >
            Log out
          </RouterLink>
          <RouterLink
            v-if="!isAuthenticated"
            to="/login"
            aria-label="Log in button"
            class="primary-mwi h-min button-padding body-3-semi-bold rounded-full max-w-max"
          >
             Log in
          </RouterLink>
        </div>
      </MyWealthInvest>

      <!-- log out, log in button -->
      <MyWealth>
        <div class="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-x-12">
          <RouterLink
            v-if="isAuthenticated"
            @click.prevent="logout"
            to="/login"
            aria-label="Log out button"
            class="primary-mwp button-padding body-3-semi-bold rounded-full max-w-max"
          >
            Log out
          </RouterLink>
          <RouterLink
            v-if="!isAuthenticated"
            to="/login"
            aria-label="Log in button"
            class="primary-mwp h-min button-padding body-3-semi-bold rounded-full max-w-max"
          >
            Log in
          </RouterLink>
        </div>
      </MyWealth>
    </nav>

    <!-- Mobile nav panel -->
    <Dialog as="div" class="lg:hidden" @close="mobileMenuOpen = false" :open="mobileMenuOpen">
      <div class="fixed inset-0 z-10 backdrop-blur-sm" />

      <DialogPanel class="bg-white w-10/12 fixed inset-y-0 z-10 overflow-y-auto shadow-md py-8 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
        <div class="flex items-center justify-between px-8">
          <a href="/" class="-m-1.5 p-1.5">
            <span class="sr-only">my wealth invest</span>
            <picture class="h-12 w-auto">
              <MyWealthInvest>
                <img
                  :srcset="APP.CdnBaseUrl + '/my-wealth-invest-logo-rebrand-light.svg'"
                  alt="my wealth invest"
                />
              </MyWealthInvest>
              <MyWealth>
                <img
                  :srcset="APP.CdnMwpBaseUrl + '/logos/MWP-logo-2.svg'"
                  alt="my wealth private"
                  class="-ml-2"
                />
              </MyWealth>
            </picture>
          </a>
          <button
            @click="mobileMenuOpen = false"
            aria-label="Close main menu"
            class="-m-2.5 rounded-md p-2.5 text-gray-700"
          >
            <span class="sr-only">Close menu</span>
            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div class="mt-6 flow-root">
          <MyWealthInvest>
            <div class="-my-6 divide-y divide-gray-500/10">
              <div class="space-y-2 py-6 px-3" v-show="!isRegisteringFromCorporate">
                <AtomButton
                  v-if="!isAuthenticated"
                  v-for="item in guestLinks"
                  :key="item.name"
                  :to="item.href"
                  @click="mobileMenuOpen = false"
                  element="RouterLink"
                  button-type="unset"
                  class="body-2-light text-off-black dark:text-white sub-nav-link"
                  :aria-label="`${item.name} button`"
                >
                  {{ item.name }}
                </AtomButton>
                <AtomButton
                  v-if="isAuthenticated"
                  v-for="item in authenticatedLinks"
                  :key="item.name"
                  :to="item.href"
                  @click="mobileMenuOpen = false"
                  element="RouterLink"
                  button-type="unset"
                  class="body-2-light text-off-black dark:text-white sub-nav-link"
                  :aria-label="`${item.name} button`"
                >
                  <span v-html="item.html ?? undefined"></span>
                  {{ item?.html == null ? item.name : '' }}
                </AtomButton>
              </div>
              <div class="py-6 px-5">
                <AtomButton
                  v-if="isAuthenticated"
                  to="/login"
                  @click.prevent="logout"
                  element="RouterLink"
                  button-type="unset"
                  class="secondary-mwi button-padding body-3-semi-bold rounded-full max-w-max flex"
                  aria-label="Log out button"
                >
                  Log out
                </AtomButton>
                <AtomButton
                  v-if="!isAuthenticated"
                  element="RouterLink"
                  to="/login"
                  aria-label="Log in button"
                  button-type="unset"
                  class="primary-mwi h-min button-padding body-3-semi-bold rounded-full flex justify-center items-center"
                >
                  Log in
                </AtomButton>
              </div>
            </div>
          </MyWealthInvest>
          <MyWealth>
            <div class="py-2 transition-height duration-9000 ease-in-out">
              <MenuNav v-if="['INVESTED', 'FUNDED', 'REGISTERED'].includes(result?.clientInfo.status) || result?.clientInfo.type === 'ADVISED' && mobileMenuOpen"
                :type="result?.clientInfo.type"
                :clientInfo="result?.clientInfo"
                :killSwitchFlags="storeKillSwitchFlags"
                :hasSelfSelectPortfolio = "hasSelfSelectPortfolio"
                :corporateClientServices = "corporateClientServices"
                :key="forceRerender"
                :currentPage="router?.currentRoute?.value?.path"
                :isAuthenticated="isAuthenticated"
                :mobileMenuOpen="mobileMenuOpen"
                v-model:mobileMenuOpen="mobileMenuOpen"
              />
            </div>
          </MyWealth>
        </div>
      </DialogPanel>
    </Dialog>
  </header>
</template>
